<template>
  <v-card flat class="mobile-profit-loss">
    <v-toolbar dense flat color="grey lighten-3" class="no-print">
      <v-btn icon :aria-label="backLabel" @click="goBack"><v-icon>mdi-arrow-left</v-icon></v-btn>
      <v-btn v-if="level !== 'summary'" icon aria-label="Back to Profit and Loss summary" @click="goHome"><v-icon>mdi-home-outline</v-icon></v-btn>
      <v-toolbar-title>{{ title }}</v-toolbar-title>
    </v-toolbar>

    <v-card-text class="mobile-profit-loss__body pa-4 pa-sm-5">
      <div class="text-caption admin-muted mb-4">
        <strong v-if="locationName">{{ locationName }}</strong><span v-if="locationName"> • </span>{{ periodLabel }}
      </div>
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-alert v-else-if="error" type="error" outlined role="alert">
        {{ error }}
        <v-btn text color="primary" min-height="44" @click="retry">Try again</v-btn>
      </v-alert>

      <template v-else-if="level === 'summary'">
        <div class="profit-summary mb-5">
          <div><span>Total income</span><strong>{{ currency(incomeTotal) }}</strong></div>
          <div><span>Gross profit</span><strong>{{ currency(grossProfit) }}</strong></div>
          <div class="profit-summary__net"><span>Net profit</span><strong>{{ currency(netProfit) }}</strong></div>
        </div>
        <v-list class="report-list pa-0" two-line>
          <v-list-item button @click="openIncome">
            <v-list-item-avatar color="success"><v-icon dark>mdi-trending-up</v-icon></v-list-item-avatar>
            <v-list-item-content><v-list-item-title class="font-weight-bold">Income</v-list-item-title><v-list-item-subtitle>{{ currency(incomeTotal) }} revenue • {{ currency(costTotal) }} cost</v-list-item-subtitle></v-list-item-content>
            <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
          </v-list-item>
          <v-divider />
          <v-list-item button @click="openExpenses">
            <v-list-item-avatar color="warning"><v-icon dark>mdi-receipt-outline</v-icon></v-list-item-avatar>
            <v-list-item-content><v-list-item-title class="font-weight-bold">Expenses</v-list-item-title><v-list-item-subtitle>{{ currency(expenseTotal) }} for this period</v-list-item-subtitle></v-list-item-content>
            <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
          </v-list-item>
        </v-list>
      </template>

      <v-alert v-else-if="!items.length" type="info" outlined>No activity was found for this selection.</v-alert>
      <v-list v-else class="report-list pa-0" two-line>
        <template v-for="(item, index) in items">
          <v-list-item :key="item.key" button @click="openItem(item)">
            <v-list-item-avatar :color="item.color || (item.receipt ? 'primary' : 'grey lighten-2')">
              <v-icon :dark="Boolean(item.receipt || item.color)" :color="item.receipt || item.color ? '' : 'primary'">{{ item.icon || (item.receipt ? 'mdi-receipt-text-outline' : 'mdi-chart-box-outline') }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="item.next || item.receipt"><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
          </v-list-item>
          <v-divider v-if="index < items.length - 1" :key="`${item.key}-divider`" />
        </template>
      </v-list>
    </v-card-text>
  </v-card>
</template>

<script>
import reportService from "@/modules/ReportView/service";

export default {
  name: "MobileProfitAndLoss",
  data: () => ({
    loading: false,
    error: "",
    level: "summary",
    title: "Profit and Loss",
    items: [],
    incomeTypes: [],
    expenses: [],
    expenseTotal: 0,
    locationName: "",
    stack: [],
    retryAction: null,
  }),
  computed: {
    locationId() { return Number(this.$route.query.LocationId); },
    start() { return this.$route.query.start; },
    end() { return this.$route.query.end; },
    periodLabel() { return `${this.$moment(this.start).format("MMM D, YYYY")} – ${this.$moment(this.end).format("MMM D, YYYY")}`; },
    incomeTotal() { return this.incomeTypes.reduce((sum, item) => sum + (Number(item.revenue) || 0), 0); },
    costTotal() { return this.incomeTypes.reduce((sum, item) => sum + (Number(item.cost) || 0), 0); },
    grossProfit() { return this.incomeTotal - this.costTotal; },
    netProfit() { return this.grossProfit - this.expenseTotal; },
    backLabel() { return this.level === "summary" ? "Back to reports" : "Back one level"; },
    baseParams() { return { LocationId: this.locationId, start: this.start, end: this.end }; },
  },
  created() { this.loadSummary(); },
  methods: {
    async loadSummary() {
      if (!Number.isSafeInteger(this.locationId) || this.locationId <= 0 || !this.$moment(this.start).isValid() || !this.$moment(this.end).isValid()) {
        this.error = "A valid location and date range are required.";
        return;
      }
      this.loading = true;
      this.error = "";
      this.retryAction = this.loadSummary;
      try {
        const [incomeResponse, expenseResponse] = await Promise.all([
          reportService.Location.profitAndLoss({ ...this.baseParams, InvoiceDetailTypes: true }),
          reportService.Location.profitAndLossExpenses(this.baseParams),
        ]);
        this.incomeTypes = (incomeResponse.data && incomeResponse.data.InvoiceDetailTypes) || [];
        this.expenseTotal = Number(expenseResponse.data && expenseResponse.data.total) || 0;
        this.expenses = (expenseResponse.data && expenseResponse.data.expenses) || [];
        this.locationName = (expenseResponse.data && expenseResponse.data.locationName) || "";
      } catch (error) {
        this.error = "Profit and Loss could not be loaded. Check your connection and try again.";
      } finally { this.loading = false; }
    },
    retry() { if (this.retryAction) this.retryAction(); },
    currency(value) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(value || 0)); },
    metricsSubtitle(item) {
      const revenue = Number(item.revenue !== undefined ? item.revenue : item.sale || item.subTotal) || 0;
      const cost = Number(item.cost) || 0;
      const quantity = Number(item.quantity) || 0;
      return `${quantity ? `${quantity} item${quantity === 1 ? "" : "s"} • ` : ""}${this.currency(revenue)} revenue • ${this.currency(cost)} cost`;
    },
    pushLevel(level, title, items) {
      this.stack.push({ level: this.level, title: this.title, items: this.items });
      this.level = level;
      this.title = title;
      this.items = items;
    },
    openIncome() {
      this.pushLevel("types", "Income", this.incomeTypes.map((item) => ({
        key: `type-${item.id}`, name: item.name, subtitle: this.metricsSubtitle(item), next: "type", raw: item,
      })));
    },
    openExpenses() {
      this.pushLevel("expenses", "Expenses", this.expenses.map((item, index) => {
        const category = item.ExpenseCategory && item.ExpenseCategory.name;
        const payee = item.Payee && item.Payee.name;
        const detail = [payee, item.note, item.referenceNumber ? `Ref #${item.referenceNumber}` : ""].filter(Boolean).join(" • ");
        return {
          key: `expense-${item.id || index}`,
          name: category || payee || "Expense",
          subtitle: `${item.enteredAt ? this.$moment(item.enteredAt).format("L LT") + " • " : ""}${this.currency(item.amount)}${detail ? ` • ${detail}` : ""}`,
          icon: "mdi-receipt-outline",
          color: "warning",
        };
      }));
    },
    async openItem(item) {
      if (item.receipt) {
        this.$router.push({ path: "/invoice/list", query: { receiptId: item.receipt, receiptMode: "active" } }).catch(() => {});
        return;
      }
      if (!item.next) return;
      this.loading = true;
      this.error = "";
      this.retryAction = () => this.openItem(item);
      try {
        let response;
        if (item.next === "type") response = await reportService.Location.profitAndLoss({ ...this.baseParams, InvoiceDetailTypeId: item.raw.id });
        if (item.next === "category") response = await reportService.Location.profitAndLoss({ ...this.baseParams, CategoryId: item.raw.id });
        if (item.next === "product") response = await reportService.Location.profitAndLoss({ ...this.baseParams, ProductId: item.raw.id, page: 1, limit: 250 });
        if (item.next === "bill") response = await reportService.Location.profitAndLoss({ ...this.baseParams, BillPaymentName: item.raw.key, page: 1, limit: 250 });
        this.openResponse(item, response.data || {});
      } catch (error) {
        this.error = "This report level could not be loaded. Try again.";
      } finally { this.loading = false; }
    },
    openResponse(parent, data) {
      const detail = data.InvoiceDetailType || {};
      if (Array.isArray(detail.Categories)) {
        this.pushLevel("categories", parent.name, detail.Categories.map((row) => ({ key: `category-${row.id}`, name: row.name, subtitle: this.metricsSubtitle(row), next: "category", raw: row })));
        return;
      }
      if (Array.isArray(detail.Groups)) {
        this.pushLevel("groups", parent.name, detail.Groups.map((row) => ({ key: `group-${row.key}`, name: row.name, subtitle: this.metricsSubtitle(row), next: "bill", raw: row })));
        return;
      }
      if (Array.isArray(data.Category && data.Category.Products)) {
        this.pushLevel("products", parent.name, data.Category.Products.map((row) => ({ key: `product-${row.id}`, name: row.name, subtitle: this.metricsSubtitle(row), next: "product", raw: row })));
        return;
      }
      const transactions = (data.Product && data.Product.Transactions) || (data.BillPaymentGroup && data.BillPaymentGroup.Transactions) || detail.InvoiceDetails || [];
      this.pushLevel("transactions", parent.name, transactions.map((row, index) => ({
        key: `transaction-${row.id || index}`, name: `#${row.invoiceIdentifier || row.name || row.InvoiceId || "Receipt"}`, subtitle: `${row.saleDate ? this.$moment(row.saleDate).format("L LT") + " • " : ""}${this.metricsSubtitle(row)}`, receipt: row.InvoiceId,
      })));
    },
    goHome() {
      this.level = "summary";
      this.title = "Profit and Loss";
      this.items = [];
      this.stack = [];
      this.error = "";
    },
    goBack() {
      const previous = this.stack.pop();
      if (!previous) { this.$router.back(); return; }
      this.level = previous.level;
      this.title = previous.title;
      this.items = previous.items;
      this.error = "";
    },
  },
};
</script>

<style scoped>
.mobile-profit-loss { display: flex; flex-direction: column; height: calc(100dvh - 48px); margin: 0 auto; max-width: 900px; overflow: hidden; }
.mobile-profit-loss > .v-toolbar { flex: none; }
.mobile-profit-loss__body { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior-y: contain; touch-action: pan-y; -webkit-overflow-scrolling: touch; }
.profit-summary { border: 1px solid var(--v-primary-base); border-radius: 14px; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); overflow: hidden; }
.profit-summary > div { display: flex; flex-direction: column; gap: 4px; padding: 14px; }
.profit-summary span { font-size: .75rem; opacity: .72; }
.profit-summary strong { font-size: 1.15rem; }
.profit-summary__net { background: var(--brand-bg); }
.report-list { border: 1px solid rgba(0, 0, 0, .14); border-radius: 14px; overflow: hidden; }
.theme--dark .report-list { border-color: rgba(255, 255, 255, .2); }
@media (max-width: 599px) { .profit-summary { grid-template-columns: 1fr; } .profit-summary > div + div { border-top: 1px solid rgba(0, 0, 0, .1); } }
</style>
